// ═══════════════════════════════════════════════════════════
// DetailView — bilingual, Apple-inspired white theme
// Per-member call (office + mobile) + email + vCard + WhatsApp
// Plus department QR code at the bottom
// ═══════════════════════════════════════════════════════════

import { motion } from "framer-motion";
import {
  ArrowRight, ArrowLeft, Mail, User, Building2,
  UserPlus, Share2, ExternalLink, QrCode,
} from "lucide-react";
import { Department, ALL, Member, floorLabel } from "@/lib/data";
import { useLang } from "@/lib/i18n";
import {
  downloadVCard, openWhatsApp, outlookWebUrl, deptDeepLink,
} from "@/lib/actions";
import DepartmentCard from "./DepartmentCard";
import QRPanel from "./QRPanel";

interface Props {
  dept: Department;
  onBack: () => void;
  onSelect: (d: Department) => void;
}

function isCallable(n?: string): boolean {
  return !!n && /^\d{6,}$/.test(n) && n !== "0000";
}

// ─── Distinct phone icons ──────────────────────────────────

interface IconProps { size?: number; className?: string; }

function DeskPhoneIcon({ size = 14, className = "" }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <path
        d="M5 7c0-1.1.9-2 2-2h1.5c.5 0 .9.3 1 .8l.6 2.4c.1.4-.1.8-.4 1L8.5 10c1 1.8 2.5 3.3 4.3 4.3l1.1-1.4c.2-.3.6-.4 1-.4l2.4.6c.5.1.8.5.8 1V16c0 1.1-.9 2-2 2-7.7 0-14-6.3-14-14"
        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <rect x="3" y="18" width="18" height="3" rx="0.5" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    </svg>
  );
}

function MobilePhoneIcon({ size = 14, className = "" }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <rect x="6" y="2" width="12" height="20" rx="2.5" stroke="currentColor" strokeWidth="2" />
      <line x1="10.5" y1="4.5" x2="13.5" y2="4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="12" cy="19" r="1" fill="currentColor" />
    </svg>
  );
}

// WhatsApp glyph (recognizable)
function WhatsAppIcon({ size = 14, className = "" }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M17.5 14.4c-.3-.1-1.7-.8-2-.9-.3-.1-.5-.1-.7.1-.2.3-.7.9-.9 1.1-.2.2-.3.2-.6.1-.3-.1-1.2-.5-2.3-1.4-.9-.7-1.5-1.7-1.6-1.9-.2-.3 0-.5.1-.6.1-.1.3-.3.4-.5.1-.2.2-.3.3-.5.1-.2 0-.4 0-.5-.1-.2-.7-1.6-.9-2.2-.2-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1 .9-1 2.3 0 1.4 1 2.7 1.1 2.9.1.2 2 3 4.8 4.2.7.3 1.2.5 1.6.6.7.2 1.3.2 1.8.1.5-.1 1.7-.7 1.9-1.3.2-.6.2-1.2.2-1.3-.1-.2-.3-.3-.6-.4zM12 22C6.5 22 2 17.5 2 12S6.5 2 12 2s10 4.5 10 10c0 1.7-.4 3.2-1.2 4.6L22 22l-5.5-1.4c-1.3.9-2.9 1.4-4.5 1.4z" />
    </svg>
  );
}

interface PersonRowProps {
  name: string;
  title?: string;
  email?: string;
  ext?: string;
  mobile?: string;
  isHead?: boolean;
  deptAr: string;
  deptDeep: string;
  floor: string;
}

function PersonRow({
  name, title, email, ext, mobile, isHead, deptAr, deptDeep, floor,
}: PersonRowProps) {
  const { t, isRtl } = useLang();

  const handleSaveContact = () => {
    downloadVCard({
      name,
      title,
      org: deptAr,
      email,
      ext,
      mobile,
      floor,
    });
  };

  const handleShare = () => {
    openWhatsApp({
      name,
      title,
      org: deptAr,
      email,
      ext,
      mobile,
      floor,
    }, deptDeep);
  };

  return (
    <div className="px-4 py-3.5">
      {/* Name + role */}
      <div className="flex items-start gap-3 mb-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0
          ${isHead ? "bg-[#1A2747]" : "bg-[rgba(74,144,217,0.10)]"}`}>
          <User size={16} className={isHead ? "text-white" : "text-[#1A2747]"} />
        </div>
        <div className="flex-1 min-w-0">
          {isHead && (
            <div className={`text-[9px] text-[#9CA3AF] mb-1 font-medium ${isRtl ? "" : "tracking-[0.6px] uppercase"}`}>
              {t.manager}
            </div>
          )}
          <div className="text-[13px] font-semibold text-[#1A2747] leading-snug">{name}</div>
          {title && (
            <div className="text-[10px] text-[#6B7280] mt-0.5 leading-snug">{title}</div>
          )}
        </div>
      </div>

      {/* Contact read-only rows */}
      <div className="flex flex-col gap-1.5" style={{ marginRight: 52 }}>
        {ext && (
          <div className="flex items-center gap-2 text-[11px] text-[#6B7280]">
            <DeskPhoneIcon size={12} className="text-[#1A2747] flex-shrink-0" />
            <span className="font-mono-floor text-[#1A2747]" style={{ direction: "ltr" }}>{ext}</span>
            <span className="text-[9px]">{t.contactOfficeLabel}</span>
          </div>
        )}
        {mobile && (
          <div className="flex items-center gap-2 text-[11px] text-[#6B7280]">
            <MobilePhoneIcon size={12} className="text-[#4A90D9] flex-shrink-0" />
            <span className="font-mono-floor text-[#1A2747]" style={{ direction: "ltr" }}>{mobile}</span>
            <span className="text-[9px]">{t.contactMobileLabel}</span>
          </div>
        )}
        {email && (
          <div className="flex items-center gap-2 text-[11px] text-[#6B7280] truncate">
            <Mail size={11} className="text-[#9CA3AF] flex-shrink-0" />
            <span className="text-[#1A2747] truncate" style={{ direction: "ltr" }}>{email}</span>
          </div>
        )}
      </div>

      {/* Primary action buttons: call / mobile / email */}
      <div className="flex gap-2 mt-3" style={{ direction: "ltr" }}>
        {isCallable(ext) && (
          <a
            href={`tel:${ext}`}
            style={{ touchAction: "manipulation" }}
            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl
              bg-[#1A2747] text-white text-[11px] font-semibold
              hover:bg-[#0F1A33] active:scale-[0.98] transition-all"
            aria-label={`${t.contactOffice} ${name}`}
          >
            <DeskPhoneIcon size={13} className="text-white" />
            <span>{t.contactOffice}</span>
          </a>
        )}
        {isCallable(mobile) && (
          <a
            href={`tel:${mobile}`}
            style={{ touchAction: "manipulation" }}
            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl
              bg-[#4A90D9] text-white text-[11px] font-semibold
              hover:bg-[#3D7BC4] active:scale-[0.98] transition-all"
            aria-label={`${t.contactMobile} ${name}`}
          >
            <MobilePhoneIcon size={13} className="text-white" />
            <span>{t.contactMobile}</span>
          </a>
        )}
        {email && (
          <a
            href={`mailto:${email}`}
            style={{ touchAction: "manipulation" }}
            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl
              bg-[rgba(74,144,217,0.12)] text-[#1A2747] text-[11px] font-semibold
              hover:bg-[rgba(74,144,217,0.20)] active:scale-[0.98] transition-all"
            aria-label={`${t.contactEmail} ${name}`}
          >
            <Mail size={13} />
            <span>{t.contactEmail}</span>
          </a>
        )}
      </div>

      {/* Secondary actions row: save contact + share */}
      <div className="flex items-center gap-2 mt-2">
        <button
          onClick={handleSaveContact}
          style={{ touchAction: "manipulation" }}
          className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl
            bg-white border border-[rgba(15,23,42,0.08)] text-[#1A2747] text-[10.5px] font-medium
            hover:border-[#1A2747] hover:bg-[rgba(15,23,42,0.03)] active:scale-[0.98] transition-all"
        >
          <UserPlus size={12} />
          <span>{t.saveContact}</span>
        </button>
        <button
          onClick={handleShare}
          style={{ touchAction: "manipulation" }}
          className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl
            bg-white border border-[rgba(15,23,42,0.08)] text-[#1A2747] text-[10.5px] font-medium
            hover:border-[#25D366] hover:text-[#128C7E] hover:bg-[rgba(37,211,102,0.05)] active:scale-[0.98] transition-all"
          aria-label={t.shareWhatsApp}
        >
          <WhatsAppIcon size={12} />
          <span>{t.shareWhatsApp}</span>
        </button>
        {email && (
          <a
            href={outlookWebUrl(email)}
            target="_blank"
            rel="noopener noreferrer"
            style={{ touchAction: "manipulation" }}
            className="flex items-center justify-center gap-1 py-2 px-3 rounded-xl
              bg-white border border-[rgba(15,23,42,0.08)] text-[#0078D4] text-[10.5px] font-medium
              hover:bg-[rgba(0,120,212,0.06)] hover:border-[#0078D4] active:scale-[0.98] transition-all flex-shrink-0"
            aria-label={t.openOutlookWeb}
            title={t.openOutlookWeb}
          >
            <ExternalLink size={11} />
            <span>OWA</span>
          </a>
        )}
      </div>
    </div>
  );
}

export default function DetailView({ dept, onBack, onSelect }: Props) {
  const { t, isRtl } = useLang();
  const fl = floorLabel(dept.floor);
  const isEmpty = !dept.ar;
  const others = ALL.filter((x) => x.floor === dept.floor && x.id !== dept.id && x.ar);
  const hasHead = !!dept.headAr;
  const Back = isRtl ? ArrowRight : ArrowLeft;
  const deepLink = deptDeepLink(dept);

  const headMember: PersonRowProps | null = hasHead
    ? {
        name: isRtl ? dept.headAr : (dept.headEn || dept.headAr),
        title: isRtl ? dept.headTitle : (dept.headTitleEn || dept.headTitle),
        email: dept.email,
        ext: dept.ext,
        mobile: dept.mobile,
        isHead: true,
        deptAr: dept.ar,
        deptDeep: deepLink,
        floor: dept.floor,
      }
    : null;

  const primary = isRtl ? (isEmpty ? fl.ar : dept.ar) : (isEmpty ? fl.en : dept.en);
  const secondary = isRtl ? (isEmpty ? fl.en : dept.en) : (isEmpty ? fl.ar : dept.ar);

  return (
    <div className="min-h-screen flex flex-col pt-12 pb-4 px-4">
      {/* Back button */}
      <motion.button
        initial={{ opacity: 0, x: isRtl ? -8 : 8 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.2 }}
        onClick={onBack}
        style={{ touchAction: "manipulation" }}
        className="flex items-center gap-1.5 text-[#1A2747] text-[12px] mb-4 font-medium
          hover:opacity-70 transition-opacity self-start"
      >
        <Back size={14} />
        <span>{t.back}</span>
      </motion.button>

      {/* Main card — department identity */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="glass-card overflow-hidden mb-3"
      >
        <div className={`h-1 ${isRtl
          ? "bg-gradient-to-l from-[#1A2747] via-[#4A90D9] to-transparent"
          : "bg-gradient-to-r from-[#1A2747] via-[#4A90D9] to-transparent"}`} />

        <div className="p-4">
          {/* Department identity */}
          <div className="flex items-start gap-3 mb-4">
            <div className="w-11 h-11 rounded-2xl flex items-center justify-center text-[18px] flex-shrink-0
              bg-[rgba(74,144,217,0.10)] text-[#1A2747]">
              {dept.icon}
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-[15px] font-bold text-[#1A2747] leading-snug">{primary}</h2>
              <p className="text-[10px] text-[#6B7280] mt-1 font-light tracking-wide">
                {secondary}
              </p>
            </div>
          </div>

          {/* FLOOR — hero element (primary purpose of the app) */}
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-l from-[#1A2747] to-[#243556]
            px-5 py-4 flex items-center justify-between">
            {/* faint watermark icon */}
            <Building2
              size={96}
              className="absolute -bottom-5 text-white opacity-[0.06] pointer-events-none"
              style={{ [isRtl ? "left" : "right"]: "-12px" } as React.CSSProperties}
            />
            <div className="relative">
              <div className="text-[10px] tracking-[2.5px] text-[#9FB4D6] font-medium uppercase mb-0.5">
                {isRtl ? "الطابق" : "FLOOR"}
              </div>
              <div className="flex items-baseline gap-2.5">
                <span className="text-[52px] font-bold font-mono-floor leading-[0.9] text-white">
                  {dept.floor}
                </span>
                {!/^\d+$/.test(dept.floor) && (
                  <span className="text-[13px] text-[#C8DBF2] font-light" style={{ direction: "ltr" }}>
                    {fl.en}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Members list */}
      {(headMember || (dept.members && dept.members.length > 0)) && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.08 }}
          className="glass-card overflow-hidden mb-3"
        >
          {headMember && (
            <>
              <PersonRow {...headMember} />
              {dept.members && dept.members.length > 0 && (
                <div className="border-t border-[rgba(15,23,42,0.06)]" />
              )}
            </>
          )}
          {dept.members?.map((m: Member, i) => (
            <div key={i}>
              <PersonRow
                name={isRtl ? m.ar : (m.en || m.ar)}
                title={isRtl ? m.titleAr : (m.titleEn || m.titleAr)}
                email={m.email}
                ext={m.ext}
                mobile={m.mobile}
                deptAr={dept.ar}
                deptDeep={deepLink}
                floor={dept.floor}
              />
              {i < (dept.members?.length ?? 0) - 1 && (
                <div className="border-t border-[rgba(15,23,42,0.06)]" />
              )}
            </div>
          ))}
        </motion.div>
      )}

      {/* QR code for the department */}
      {hasHead && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.16 }}
          className="glass-card mb-3"
        >
          <div className="flex items-center gap-2 px-4 pt-3">
            <QrCode size={13} className="text-[#1A2747]" />
            <span className="text-[10px] text-[#1A2747] font-semibold">{t.qrTitle}</span>
          </div>
          <QRPanel
            value={deepLink}
            label={isRtl ? dept.ar : dept.en}
            sublabel={t.qrSubtitle}
          />
        </motion.div>
      )}

      {/* Other departments on same floor */}
      {others.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
        >
          <div className="flex items-center gap-2 mb-2.5 mt-2">
            <Building2 size={13} className="text-[#6B7280]" />
            <div className="text-[10px] text-[#6B7280] font-medium">{t.otherDeptsOnFloor}</div>
          </div>
          <div className="flex flex-col gap-2">
            {others.map((x, i) => (
              <DepartmentCard
                key={x.id}
                dept={x}
                index={i}
                onClick={() => onSelect(x)}
              />
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
}
